import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { Box, Grid, GridProps, SxProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import { NO_DRAG_SELECTOR } from "../../../../app/constants";
import {
  FormSectionHeader,
  StyledDialogContentWrapper,
} from "../../styled/styled";
import { CustomButton } from "../customButton/CustomButton";
import InfoTooltip from "../InfoTooltip/InfoTooltip";
import OverlaySpinner from "../overlaySpinner/OverlaySpinner";

type Props = {
  children: any;
  title?: string | null | React.ReactNode;
  titleInside?: boolean;
  infoText?: string | null;
  onSave?: () => void;
  onCancel?: () => void;
  onDelete?: () => void;
  margin?: boolean;
  saveDisabled?: boolean;
  isLoading?: boolean;
  edit?: boolean;
  containerSx?: SxProps;
  childContainerSx?: SxProps;
  buttonsContainerSx?: SxProps;
  GridContainerProps?: GridProps;
  customSaveButtonLabel?: string | null;
  customCancelButtonLabel?: string | null;
  customDeleteButtonLabel?: string | null;
  headerSx?: SxProps;
  draggable?: boolean;
};

const DialogContentWrapper = ({
  children,
  title = "",
  infoText,
  onSave,
  onCancel,
  onDelete,
  margin = false,
  saveDisabled = false,
  isLoading = false,
  containerSx,
  childContainerSx,
  buttonsContainerSx = {},
  GridContainerProps = {},
  customSaveButtonLabel,
  customCancelButtonLabel,
  customDeleteButtonLabel,
  headerSx,
  draggable = true,
}: Props) => {
  const { t } = useTranslation();
  const renderButtonContainer = onCancel || onDelete || onSave;

  const cursor = draggable ? "grab" : "auto";

  return (
    <StyledDialogContentWrapper sx={containerSx}>
      <OverlaySpinner
        isLoading={isLoading}
        sx={{ position: "absolute", borderRadius: "inherit" }}
      />
      <Box
        display="flex"
        justifyContent="space-between"
        minHeight="35px"
        marginBottom={margin ? "1rem" : "unset"}
        sx={{ cursor, flexShrink: 0 }}
      >
        <FormSectionHeader
          sx={headerSx}
          variant="header"
          display="flex"
          alignItems="center"
        >
          {title}
          {infoText && (
            <InfoTooltip title={infoText} style={{ marginLeft: "0.5rem" }} />
          )}
        </FormSectionHeader>
      </Box>

      <Grid
        className={NO_DRAG_SELECTOR}
        container
        columnSpacing={2}
        rowSpacing={1}
        sx={childContainerSx}
        {...GridContainerProps}
      >
        {children}
      </Grid>
      {renderButtonContainer && (
        <Box
          width="100%"
          marginLeft="auto"
          marginRight="auto"
          marginTop="2rem"
          display="flex"
          justifyContent="space-between"
          sx={{ cursor, flexShrink: 0, ...buttonsContainerSx }}
        >
          {onCancel && (
            <CustomButton
              sx={{ height: "36px", borderRadius: "20px" }}
              startIcon={<CloseIcon />}
              onClick={onCancel}
              variant="outlined"
            >
              {customCancelButtonLabel || t("common:cancelShort")}
            </CustomButton>
          )}
          {onDelete && (
            <CustomButton
              sx={{ height: "36px", borderRadius: "20px" }}
              startIcon={<CloseIcon />}
              onClick={onDelete}
              variant="outlined"
              color="error"
            >
              {customDeleteButtonLabel || t("common:delete")}
            </CustomButton>
          )}
          {onSave && (
            <CustomButton
              disabled={saveDisabled}
              sx={{ height: "36px", borderRadius: "20px" }}
              startIcon={<CheckIcon />}
              onClick={onSave}
              variant="contained"
            >
              {customSaveButtonLabel || t("common:complete")}
            </CustomButton>
          )}
        </Box>
      )}
    </StyledDialogContentWrapper>
  );
};

export default DialogContentWrapper;
